import { api } from '../../lib/api';
import type {
  Account,
  AccountInput,
  Currency,
} from '../../types/account';
import type { AccountGridQuery, AccountGridResponse } from '../../types/accountGrid';
import type { AccountHistoryPage } from '../../types/accountHistory';
import type { ApiConnections } from '../../types/apiConnections';
import type { CurrentUser, GridPreferences, UserRole } from '../../types/user';

export type AccountDetail = {
  account: Account;
  resolvedCurrency: Currency;
  ancestors: Account[];
  children: Account[];
};

export async function listAccounts(): Promise<Account[]> {
  const res = await api<{ accounts: Account[] }>('/api/accounts');
  return res.accounts;
}

/** Whether the automotive report-template industry default is on (server flag). */
export async function getReportTemplateConfig(): Promise<{ automotiveDefaultOn: boolean }> {
  return api<{ automotiveDefaultOn: boolean }>('/api/accounts/report-template-config');
}

export async function getAccountDetail(id: string): Promise<AccountDetail> {
  return api<AccountDetail>(`/api/accounts/${id}`);
}

// Account page "API Connections" section — live Zernio platform state for the
// account's profile. refresh=true busts the server's 5-minute cache.
export async function getApiConnections(id: string, refresh = false): Promise<ApiConnections> {
  return api<ApiConnections>(`/api/accounts/${id}/api-connections${refresh ? '?refresh=1' : ''}`);
}

// Reset a wedged / ghost-profile account to un-onboarded — clears the Zernio
// linkage (best-effort deletes the now-often-gone Zernio profile) so the account
// can be re-onboarded fresh or deleted. The endpoint lives on the onboarding
// router (it owns Zernio mutations), but the action is account-scoped.
export async function resetAccountConnection(accountId: string): Promise<Account> {
  const res = await api<{ account: Account }>('/api/onboarding/reset-connection', {
    method: 'POST',
    body: JSON.stringify({ accountId }),
  });
  return res.account;
}

export async function createAccount(input: AccountInput): Promise<Account> {
  const res = await api<{ account: Account }>('/api/accounts', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return res.account;
}

export async function updateAccount(
  id: string,
  patch: Partial<AccountInput>,
): Promise<Account> {
  const res = await api<{ account: Account }>(`/api/accounts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return res.account;
}

export type DeletePreview = {
  account: Account;
  childrenCount: number;
  contactsLinked: number;
};

export async function getDeletePreview(id: string): Promise<DeletePreview> {
  return api<DeletePreview>(`/api/accounts/${id}/delete-preview`);
}

export type DeleteResult = {
  ok: true;
  contactsUnlinked: number;
  parentAccountId: string | null;
};

export async function deleteAccount(id: string): Promise<DeleteResult> {
  return api<DeleteResult>(`/api/accounts/${id}`, { method: 'DELETE' });
}

// Stage 5A — Accounts grid. Filters serialise to a JSON `filters` query param;
// sort/page are plain params (mirrors the server parse in router.ts).
export async function getAccountsGrid(query: AccountGridQuery): Promise<AccountGridResponse> {
  const params = new URLSearchParams();
  if (query.filters && Object.keys(query.filters).length > 0) {
    params.set('filters', JSON.stringify(query.filters));
  }
  const search = query.search?.trim();
  if (search) params.set('search', search);
  params.set('sortKey', query.sortKey);
  params.set('sortDir', query.sortDir);
  params.set('page', String(query.page));
  params.set('pageSize', String(query.pageSize));
  return api<AccountGridResponse>(`/api/accounts/grid?${params.toString()}`);
}

export type BulkAssignManagerResult = {
  updated: number;
  errors: Array<{ accountId: string; reason: string }>;
};

export async function bulkAssignManager(
  accountIds: string[],
  accountManagerId: string | null,
): Promise<BulkAssignManagerResult> {
  return api<BulkAssignManagerResult>(`/api/accounts/bulk/assign-manager`, {
    method: 'POST',
    body: JSON.stringify({ accountIds, accountManagerId }),
  });
}

// Build A: bulk enrol/remove accounts from the recurring reporting sync.
export async function bulkSetReportingSync(
  accountIds: string[],
  enabled: boolean,
): Promise<BulkAssignManagerResult> {
  return api<BulkAssignManagerResult>(`/api/accounts/bulk/reporting-sync`, {
    method: 'POST',
    body: JSON.stringify({ accountIds, enabled }),
  });
}

export type BulkRefreshConnectionsResult = {
  refreshed: number;
  skipped: number;
  failed: number;
  results: Array<{
    accountId: string;
    status: 'refreshed' | 'skipped' | 'failed';
    reason?: string;
  }>;
};

// Manual bulk connection-status refresh (page / selected rows). The server
// re-resolves each account's live Zernio status, concurrency-capped, and writes
// the stored booleans + lastCheckedAt.
export async function bulkRefreshConnections(
  accountIds: string[],
): Promise<BulkRefreshConnectionsResult> {
  return api<BulkRefreshConnectionsResult>(`/api/accounts/bulk/refresh-connections`, {
    method: 'POST',
    body: JSON.stringify({ accountIds }),
  });
}

// Lightweight staff/admin user list for AM pickers + filters (GET /api/users).
// Kept in this module (rather than importing from rollouts) so the accounts
// module doesn't reach across a sibling module boundary for a generic call.
export async function listUsers(role?: UserRole, includeUid?: string): Promise<CurrentUser[]> {
  const p = new URLSearchParams();
  if (role) p.set('role', role);
  if (includeUid) p.set('includeUid', includeUid);
  const qs = p.toString() ? `?${p}` : '';
  const res = await api<{ users: CurrentUser[] }>(`/api/users${qs}`);
  return res.users;
}

// Writes one grid's preferences slice (Stage 5A §2.2). Kept in this module so
// the accounts grid persists presets without importing the rollouts module.
export async function patchMyPreferences(
  gridKey: string,
  preferences: GridPreferences,
): Promise<CurrentUser> {
  const res = await api<{ user: CurrentUser }>(`/api/me/preferences`, {
    method: 'PATCH',
    body: JSON.stringify({ gridKey, preferences }),
  });
  return res.user;
}

/**
 * "Reset": drop this user's PERSONAL view for one grid, so they fall back to the
 * team default (or the built-in). The response carries the re-resolved user, so
 * the caller can re-seed its local state from what it now inherits.
 */
export async function clearMyPreferences(gridKey: string): Promise<CurrentUser> {
  const res = await api<{ user: CurrentUser }>(
    `/api/me/preferences/${encodeURIComponent(gridKey)}`,
    { method: 'DELETE' },
  );
  return res.user;
}

/**
 * ADMIN ONLY: make this view the org-wide default for a grid. Everyone who has
 * not personalised that grid inherits it on their next load, and Reset lands
 * them on it. Audited server-side.
 */
export async function saveTeamPreferences(
  gridKey: string,
  preferences: GridPreferences,
): Promise<void> {
  await api<{ gridKey: string; preferences: GridPreferences }>(`/api/team-preferences`, {
    method: 'PUT',
    body: JSON.stringify({ gridKey, preferences }),
  });
}

// Account History tab — one page, newest first. Pass the previous page's
// nextCursor to get the next (older) page.
export async function getAccountHistory(
  id: string,
  cursor: string | null,
): Promise<AccountHistoryPage> {
  const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  return api(`/api/account-history/${encodeURIComponent(id)}${qs}`);
}
